import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../api/created/changeLog.ts';
import { ChangeLogSection } from '../../../../api/created/changeLog.ts';
import { LabOrderDtoType, LabOrderResultDtoType, type UserDto } from '../../../../api/generated/model';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../components/history/historyTypes.ts';
import { getLabOrderResultDtoTypeName, getLabOrderTypeName } from '../../../../utils/labOrder.ts';
import { sortPatientHistoryChanges } from './patientHistoryChangeOrder.ts';
import { getPatientHistoryFieldLabel } from './patientHistoryFieldLabels.ts';

export const PatientHistorySectionFilter = {
  ALL: 'ALL',
  CASE_MANAGEMENT: ChangeLogSection.CASE_MANAGEMENT,
  DIAGNOSTICS: ChangeLogSection.DIAGNOSTICS,
  THERAPY: ChangeLogSection.THERAPY,
} as const;

export type PatientHistorySectionFilter =
  (typeof PatientHistorySectionFilter)[keyof typeof PatientHistorySectionFilter];

export type PatientHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  section: PatientHistorySection;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
  subject: string;
  showSectionInTitle: boolean;
};

export type PatientHistorySection =
  | ChangeLogSection.CASE_MANAGEMENT
  | ChangeLogSection.DIAGNOSTICS
  | ChangeLogSection.THERAPY;

export type PatientHistoryMappingContext = {
  users: UserDto[];
};

export const PATIENT_HISTORY_SECTION_FILTERS = [
  PatientHistorySectionFilter.ALL,
  PatientHistorySectionFilter.CASE_MANAGEMENT,
  PatientHistorySectionFilter.DIAGNOSTICS,
  PatientHistorySectionFilter.THERAPY,
] as const;

const PATIENT_HISTORY_SECTIONS = [
  ChangeLogSection.CASE_MANAGEMENT,
  ChangeLogSection.DIAGNOSTICS,
  ChangeLogSection.THERAPY,
] as const;

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurde erstellt von',
  updated: 'wurde geändert von',
  deleted: 'wurde gelöscht von',
};

const SECTION_FILTER_LABELS: Record<PatientHistorySectionFilter, string> = {
  [PatientHistorySectionFilter.ALL]: 'Alle',
  [PatientHistorySectionFilter.CASE_MANAGEMENT]: 'Fallmanagement',
  [PatientHistorySectionFilter.DIAGNOSTICS]: 'Diagnostik',
  [PatientHistorySectionFilter.THERAPY]: 'Therapie',
};

const DIAGNOSTICS_ENTITY_TITLE_BY_KEY: Record<string, { created: string; default: string }> = {
  CLINICAL_REPORT: { created: 'Neues MKB für', default: 'MKB von' },
  LAB_REPORT: { created: 'Neues MLB für', default: 'MLB von' },
};

const THERAPY_ENTITY_TITLE_BY_KEY: Record<string, string> = {
  THERAPY: 'Therapie von',
  THERAPY_CONDITION: 'Befinden von',
  THERAPY_COMPLIANCE: 'Compliance von',
};

const THERAPY_SUBTYPE_TITLE_BY_KEY: Record<string, string> = {
  PATIENT_HOSPITAL: 'Hospitalisation von',
  PATIENT_ISOLATION: 'Isolation von',
  THERAPY_DOT: 'DOT von',
};

const HISTORY_SUBTYPE_LABEL_BY_KEY: Record<string, string> = {
  PATIENT_HOSPITAL: 'Hospitalisation',
  PATIENT_ISOLATION: 'Isolation',
};

function mapOperationToAction(operation: string | null): HistoryAction {
  const normalizedOperation = operation?.trim().toUpperCase() ?? '';

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function mapFieldChange(change: ChangeLogFieldChangeDto): HistoryFieldChange {
  const fieldLabel = getPatientHistoryFieldLabel(change.fieldName);
  const subTypeLabel = getHistoryChangeSubTypeLabel(change.subType);

  return {
    fieldLabel: subTypeLabel ? `${fieldLabel} (${subTypeLabel})` : fieldLabel,
    oldValue: change.oldValue ?? '',
    newValue: change.newValue ?? '',
  };
}

function resolveChangedByName(changedBy: string | null, users: UserDto[]) {
  if (!changedBy) {
    return '';
  }

  const user = users.find((candidate) => candidate.id === changedBy);
  if (!user) {
    return changedBy;
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return name || changedBy;
}

function isPatientHistorySection(section: string | null): section is PatientHistorySection {
  if (!section) {
    return false;
  }

  return PATIENT_HISTORY_SECTIONS.includes(section as PatientHistorySection);
}

function getNormalizedHistoryKeys(...values: (string | null)[]) {
  return values
    .filter((value): value is string => !!value)
    .flatMap((value) => {
      const normalizedValue = value.trim().toUpperCase();
      return [normalizedValue, normalizedValue.replace(/[^A-Z0-9]/g, '')];
    });
}

function getDiagnosticsEntityTitle(entry: ChangeLogEntryDto, action: HistoryAction) {
  const diagnosticsEntityTitle = getNormalizedHistoryKeys(entry.entityType, entry.subType)
    .map((value) => DIAGNOSTICS_ENTITY_TITLE_BY_KEY[value])
    .find(Boolean);

  if (!diagnosticsEntityTitle) {
    return undefined;
  }

  return action === 'created' ? diagnosticsEntityTitle.created : diagnosticsEntityTitle.default;
}

function getTherapyEntityTitle(entry: ChangeLogEntryDto) {
  return getNormalizedHistoryKeys(entry.entityType)
    .map((value) => THERAPY_ENTITY_TITLE_BY_KEY[value])
    .find(Boolean);
}

function getTherapySubTypeTitle(entry: ChangeLogEntryDto) {
  return getNormalizedHistoryKeys(entry.subType)
    .map((value) => THERAPY_SUBTYPE_TITLE_BY_KEY[value])
    .find(Boolean);
}

function isLabOrderEntity(entry: ChangeLogEntryDto) {
  return getNormalizedHistoryKeys(entry.entityType).includes('LAB_ORDER');
}

function isLabOrderDtoType(value: string): value is LabOrderDtoType {
  return Object.values(LabOrderDtoType).includes(value as LabOrderDtoType);
}

function isLabOrderResultDtoType(value: string): value is LabOrderResultDtoType {
  return Object.values(LabOrderResultDtoType).includes(value as LabOrderResultDtoType);
}

function formatUnknownLabOrderSubType(subType: string) {
  return subType
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getHistoryChangeSubTypeLabel(subType: string | null) {
  const normalizedSubType = subType?.trim().toUpperCase();
  if (!normalizedSubType) {
    return undefined;
  }

  const historySubTypeLabel = HISTORY_SUBTYPE_LABEL_BY_KEY[normalizedSubType];
  if (historySubTypeLabel) {
    return historySubTypeLabel;
  }

  if (isLabOrderResultDtoType(normalizedSubType)) {
    return getLabOrderResultDtoTypeName(normalizedSubType) ?? formatUnknownLabOrderSubType(normalizedSubType);
  }

  return formatUnknownLabOrderSubType(normalizedSubType);
}

function getLabOrderTitle(entry: ChangeLogEntryDto) {
  if (!isLabOrderEntity(entry)) {
    return undefined;
  }

  const subType = entry.subType?.trim().toUpperCase();
  if (!subType) {
    return 'Test für';
  }

  const subTypeLabel = isLabOrderDtoType(subType)
    ? getLabOrderTypeName(subType)
    : formatUnknownLabOrderSubType(subType);
  return `${subTypeLabel} für`;
}

function getHistoryEntryTitle(entry: ChangeLogEntryDto, section: PatientHistorySection, action: HistoryAction) {
  if (section === ChangeLogSection.DIAGNOSTICS) {
    const labOrderTitle = getLabOrderTitle(entry);
    if (labOrderTitle) {
      return { subject: labOrderTitle, actionLabel: ACTION_LABELS[action], showSectionInTitle: false };
    }

    const diagnosticsEntityTitle = getDiagnosticsEntityTitle(entry, action);
    if (diagnosticsEntityTitle) {
      return {
        subject: diagnosticsEntityTitle,
        actionLabel: action === 'created' ? 'erfasst von' : ACTION_LABELS[action],
        showSectionInTitle: false,
      };
    }

    return { subject: 'Diagnostik von', actionLabel: ACTION_LABELS[action], showSectionInTitle: false };
  }

  if (section === ChangeLogSection.THERAPY) {
    return {
      subject: getTherapySubTypeTitle(entry) ?? getTherapyEntityTitle(entry) ?? 'Therapie von',
      actionLabel: ACTION_LABELS[action],
      showSectionInTitle: false,
    };
  }

  return { subject: 'Der TB-Fall von', actionLabel: ACTION_LABELS[action], showSectionInTitle: true };
}

export function getPatientHistorySectionFilterName(filter: PatientHistorySectionFilter) {
  return SECTION_FILTER_LABELS[filter];
}

export function filterPatientHistoryEntries(
  entries: PatientHistoryEntry[],
  filter: PatientHistorySectionFilter,
): PatientHistoryEntry[] {
  if (filter === PatientHistorySectionFilter.ALL) {
    return entries;
  }

  return entries.filter((entry) => entry.section === filter);
}

function mapChangeLogEntryToPatientHistoryEntry(
  entry: ChangeLogEntryDto,
  context: PatientHistoryMappingContext,
): PatientHistoryEntry | null {
  if (!isPatientHistorySection(entry.section)) {
    return null;
  }

  const action = mapOperationToAction(entry.operation);
  const title = getHistoryEntryTitle(entry, entry.section, action);

  return {
    id: entry.changeSetId,
    section: entry.section,
    date: entry.changedAt,
    action,
    actionLabel: title.actionLabel,
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    subject: title.subject,
    showSectionInTitle: title.showSectionInTitle,
    changes: sortPatientHistoryChanges(entry).map(mapFieldChange),
  };
}

export function mapChangeLogEntriesToPatientHistory(
  entries: ChangeLogEntryDto[],
  context: PatientHistoryMappingContext,
): PatientHistoryEntry[] {
  return entries
    .map((entry) => mapChangeLogEntryToPatientHistoryEntry(entry, context))
    .filter((entry): entry is PatientHistoryEntry => entry !== null)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export type PatientFieldChange = HistoryFieldChange;
